import { Loader } from '@oriola-corporation/kronan-icons';
import React from 'react';
import { cn } from '../../utils';

export type ButtonVariant = 'primary' | 'secondary' | 'tertiary';
export type ButtonSize = 'default' | 'small';
export type ButtonIconPosition = 'left' | 'right';
export type ButtonIcon = {
  component: React.ReactNode;
  position?: ButtonIconPosition;
};

export type ButtonProps = Omit<React.ButtonHTMLAttributes<HTMLButtonElement>, 'children'> & {
  variant?: ButtonVariant;
  size?: ButtonSize;
  icon?: ButtonIcon;
  isVisuallyDisabled?: boolean;
  isLoading?: boolean;
  isInverted?: boolean; // This is only used for the tertiary variant, ignored for primary/secondary
  children: React.ReactNode;
};

const ButtonComponent = React.forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      variant = 'primary',
      size = 'default',
      icon,
      isVisuallyDisabled = false,
      isLoading = false,
      isInverted,
      className,
      children,
      onClick,
      ...props
    },
    ref
  ) => {
    const hasIcon = !!icon;
    const iconPosition = hasIcon ? (icon?.position ?? 'right') : undefined;
    const isDisabled = isVisuallyDisabled || props.disabled;
    const buttonVariant = getButtonVariant(variant, hasIcon, isInverted);
    const buttonSize = getButtonSize(buttonVariant, size);

    // Prevent clicks when button is loading
    const handleClick = (e: React.MouseEvent<HTMLButtonElement>) => {
      if (isLoading) {
        e.preventDefault();
        e.stopPropagation();
        return;
      }
      onClick?.(e);
    };

    return (
      <button
        ref={ref}
        className={cn(
          'ripple flex items-center justify-center gap-1 rounded-full text-base font-medium',
          'min-w-[100px]',
          'cursor-pointer border-none outline outline-offset-0 outline-transparent',
          buttonSize === 'default' && 'px-5 py-2',
          buttonSize === 'small' && 'px-4 py-1',
          buttonSize === 'defaultTertiary' && 'px-3 py-2',
          buttonSize === 'smallTertiary' && 'px-3 py-1',
          buttonSize === 'extraSmallTertiary' && 'px-2 py-0.5',
          buttonVariant === 'primary' &&
            'bg-primary text-white shadow-button hover:bg-primaryDarker',
          buttonVariant === 'secondary' &&
            'bg-white text-primary ring-2 ring-inset ring-primary hover:bg-green-50 focus-visible:ring-transparent',
          buttonVariant === 'tertiary' && 'bg-transparent text-primary hover:bg-green-50',
          buttonVariant === 'tertiaryOutlined' &&
            'bg-transparent text-primary ring-1 ring-inset ring-primary hover:bg-green-50 focus-visible:ring-transparent',
          buttonVariant === 'tertiaryInverted' && 'bg-transparent text-white hover:bg-white/10',
          buttonVariant === 'tertiaryOutlinedInverted' &&
            'bg-transparent text-white ring-1 ring-inset ring-white hover:bg-white/10 focus-visible:ring-transparent',
          isDisabled &&
            'ripple-none cursor-not-allowed bg-white text-gray-500 shadow-none ring-1 ring-inset ring-gray-100 hover:bg-white',
          isLoading && 'cursor-not-allowed',
          className
        )}
        aria-disabled={isDisabled || isLoading}
        aria-label={isLoading ? 'Laddar' : props['aria-label']}
        onClick={handleClick}
        {...props}
      >
        {hasIcon && iconPosition === 'left' && !isLoading && (
          <span className="flex items-center">{icon.component}</span>
        )}
        {isLoading ? <Loader isRotating className="my-1" /> : children}
        {hasIcon && iconPosition === 'right' && !isLoading && (
          <span className="flex items-center">{icon.component}</span>
        )}
      </button>
    );
  }
);

type ExtendedButtonVariant =
  | ButtonVariant
  | 'tertiaryOutlined'
  | 'tertiaryInverted'
  | 'tertiaryOutlinedInverted';

const getButtonVariant = (
  variant: ButtonVariant,
  hasIcon: boolean,
  isInverted?: boolean
): ExtendedButtonVariant => {
  if (variant === 'primary' || variant === 'secondary') {
    return variant;
  }

  if (isInverted) {
    if (hasIcon) {
      return 'tertiaryInverted';
    } else {
      return 'tertiaryOutlinedInverted';
    }
  }

  if (!hasIcon) {
    return 'tertiaryOutlined';
  }

  return 'tertiary';
};

type ExtendedButtonSize = ButtonSize | 'defaultTertiary' | 'smallTertiary' | 'extraSmallTertiary';

const getButtonSize = (variant: ExtendedButtonVariant, size: ButtonSize): ExtendedButtonSize => {
  if (variant === 'primary' || variant === 'secondary') {
    return size;
  }

  if (variant === 'tertiaryOutlined' || variant === 'tertiaryOutlinedInverted') {
    if (size === 'default') {
      return 'defaultTertiary';
    } else {
      return 'smallTertiary';
    }
  } else {
    return 'extraSmallTertiary';
  }
};

ButtonComponent.displayName = 'Button';

/**
 * A flexible button component with support for multiple variants, sizes, and icons.
 *
 * @param {ButtonProps} props - Button component props
 * @param {ButtonVariant} [props.variant] - Visual style variant (Primary, Secondary, Tertiary)
 * @param {ButtonSize} [props.size] - Button size (Default, Small)
 * @param {ButtonIcon} [props.icon] - Icon to display and its position (left or right)
 * @param {boolean} [props.isLoading] - Show a loading spinner instead of the children
 * @param {boolean} [props.isInverted] - Invert colors for tertiary variant (only available for tertiary)
 * @param {boolean} [props.isVisuallyDisabled] - Makes the button visually disabled while keeping it clickable
 * @param {React.Ref<HTMLButtonElement>} ref - Forwarded ref to the underlying button element
 *
 * For cases where you only need an icon, use the IconButton component instead.
 */
export const Button = ButtonComponent;
